import React from "react";
import { Box, Skeleton } from "@mui/material";
import InvestorCard, { Investor } from "./InvestorCard";
import { useMembership } from "../../contexts/MembershipContext";
import { useSuperInvestorHoldingsList } from "./hooks/useSuperInvestorHoldings";

interface InvestorGridProps {
  investors: Investor[];
  onInvestorClick?: (investor: Investor) => void;
}

const InvestorGrid: React.FC<InvestorGridProps> = ({
  investors,
  onInvestorClick,
}) => {
  const { isMembershipActive } = useMembership();
  const accessibleInvestors = investors.filter(
    (investor) => isMembershipActive || investor.cik === "0001067983",
  );
  const holdings = useSuperInvestorHoldingsList(
    accessibleInvestors.map((investor) => investor.cik),
  );
  const portfolioValues = new Map(
    accessibleInvestors.map((investor, index) => {
      const value = holdings[index].data?.totalPortfolioValue;
      return [
        investor.cik,
        value !== undefined && Number.isFinite(value) ? value : -1,
      ];
    }),
  );
  const sortedInvestors = [...investors].sort(
    (a, b) =>
      (portfolioValues.get(b.cik) ?? -1) -
      (portfolioValues.get(a.cik) ?? -1),
  );
  // Resolve the initial order before revealing cards so they do not move
  // underneath the pointer as individual portfolio requests finish.
  const sorting = holdings.some((query) => query.isLoading);
  return (
    <Box
      aria-busy={sorting}
      aria-label="Super investors, largest reported portfolios first"
      sx={{
        width: "100%",

        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)", // 1 column on mobile
          sm: "repeat(2, minmax(0, 1fr))", // 2 columns on small tablets
          md: "repeat(2, minmax(0, 1fr))", // 2 columns on medium screens (reduced for more room)
          lg: "repeat(3, minmax(0, 1fr))", // 3 columns on large screens (reduced for more room)
          xl: "repeat(4, minmax(0, 1fr))", // 4 columns on extra large screens (reduced for more room)
        },
        // Ensure consistent card sizing
        "& > *": {
          minWidth: 0, // Prevent grid items from overflowing
        },
      }}
    >
      {sorting &&
        investors.map((investor) => (
          <Skeleton
            key={investor.cik}
            variant="rounded"
            height={164}
            sx={{ borderRadius: 3 }}
          />
        ))}
      {!sorting &&
        sortedInvestors.map((investor) => (
          <InvestorCard
            key={investor.cik}
            investor={investor}
            onClick={onInvestorClick}
          />
        ))}
    </Box>
  );
};

export default InvestorGrid;
